// OGC-531 shared custom Select (SHARED-DROPDOWN.md). A controlled combobox
// trigger with a listbox popup: focus stays on the trigger and
// aria-activedescendant names the active option. ArrowUp/Down, Home/End and
// type-ahead move the active option; Enter/Space choose; Escape cancels; Tab
// closes without choosing; an outside click closes without stealing focus.
// onChange receives the chosen option's value and fires only on a change.
//
// The listbox stays in the DOM while collapsed (hidden, beside the trigger)
// and is portaled into the top layer while open. `required` is enforced on
// the enclosing form's submit: an empty or stale value blocks the submit,
// marks the trigger invalid, focuses it and shows `requiredMessage`. No
// hidden native control is rendered, so dialog focus traps never see one.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  claimOpenDropdown,
  dropdownPortalTarget,
  focusWithoutScroll,
  positionDropdown,
  showInTopLayer,
  subscribeDropdownDismissal,
  TYPE_AHEAD_RESET_MS,
  typeAheadMatch,
} from './dropdown-popup.js';
import './dropdown.css';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="m5 12 4 4 10-10" />
    </svg>
  );
}

// Flattens `options` into choosable items. An entry with an `options` array
// is a labelled group (the native <optgroup>): its items carry `group`.
export function normalizeSelectOptions(options) {
  if (!Array.isArray(options)) return [];
  const items = [];
  function add(option, group) {
    if (!option || option.value === undefined || option.value === null) return;
    items.push({
      value: String(option.value),
      label: String(option.label ?? option.value),
      disabled: option.disabled === true,
      group,
    });
  }
  for (const option of options) {
    if (option && Array.isArray(option.options)) {
      const group = String(option.label || '');
      option.options.forEach((child) => add(child, group));
    } else {
      add(option, '');
    }
  }
  return items;
}

// Consecutive items sharing a group render inside one role="group".
function groupedSegments(items) {
  const segments = [];
  items.forEach((item, index) => {
    const last = segments[segments.length - 1];
    if (last && last.group === item.group) last.entries.push({ item, index });
    else segments.push({ group: item.group, entries: [{ item, index }] });
  });
  return segments;
}

// Index of the next enabled item from the active one, wrapping; -1 if none.
export function steppedSelectIndex(items, activeIndex, step) {
  const enabled = items.map((item, index) => (item.disabled ? -1 : index)).filter((index) => index >= 0);
  if (!enabled.length) return -1;
  const position = enabled.indexOf(activeIndex);
  if (position < 0) return step > 0 ? enabled[0] : enabled[enabled.length - 1];
  return enabled[(position + step + enabled.length) % enabled.length];
}

// The visible field name of a trigger labelled by a <label> (implicit or
// htmlFor), excluding the trigger's own value and the hidden listbox, so the
// combobox name is the label rather than "Label + current value".
const LABEL_TEXT_SKIP = '.bl-select, select, button, input, textarea, small, [aria-hidden="true"]';

function labelTextFor(trigger) {
  function collect(node) {
    if (node.nodeType === 3) return node.nodeValue;
    if (node.nodeType !== 1 || node.matches(LABEL_TEXT_SKIP)) return '';
    return [...node.childNodes].map(collect).join('');
  }
  return [...(trigger?.labels || [])]
    .map((label) => [...label.childNodes].map(collect).join('').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' ');
}

function suppressNextSpaceKeyUp() {
  if (typeof window === 'undefined') return;
  function handleKeyUp(event) {
    if (event.key !== ' ') return;
    event.preventDefault();
    window.removeEventListener('keyup', handleKeyUp, true);
  }
  window.addEventListener('keyup', handleKeyUp, true);
  setTimeout(() => window.removeEventListener('keyup', handleKeyUp, true), 1000);
}

export default function Select({
  id,
  value,
  onChange,
  options,
  disabled = false,
  required = false,
  placeholder = '',
  className = '',
  triggerClassName = '',
  autoFocus = false,
  title,
  style,
  requiredMessage = 'Select an option.',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}) {
  const generatedId = useId().replaceAll(':', '');
  const triggerId = id || `bl-select-${generatedId}`;
  const listId = `bl-select-list-${generatedId}`;
  const errorId = `${triggerId}-required`;
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const typeAheadRef = useRef({ buffer: '', at: 0 });
  const releaseRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [derivedLabel, setDerivedLabel] = useState('');
  const [invalidFromForm, setInvalidFromForm] = useState(false);
  const items = normalizeSelectOptions(options);
  const stringValue = value === undefined || value === null ? '' : String(value);
  const selectedIndex = items.findIndex((item) => item.value === stringValue);
  const selected = selectedIndex >= 0 ? items[selectedIndex] : null;
  const emptyItem = items.find((item) => item.value === '');
  // An unmatched (stale) value shows the placeholder or the empty choice and
  // is never echoed into the markup.
  const displayLabel = selected ? selected.label : (placeholder || emptyItem?.label || '');
  const satisfied = Boolean(selected && selected.value !== '');
  const accessibleName = ariaLabel || derivedLabel || undefined;

  function close({ restoreFocus = true } = {}) {
    releaseRef.current?.();
    releaseRef.current = null;
    setOpen(false);
    if (restoreFocus) focusWithoutScroll(triggerRef.current);
  }

  function openList(target = 'selected') {
    const first = steppedSelectIndex(items, -1, 1);
    if (disabled || first < 0) return;
    let next = selected && !selected.disabled ? selectedIndex : first;
    if (target === 'first') next = first;
    if (target === 'last') next = steppedSelectIndex(items, -1, -1);
    setActiveIndex(next);
    if (!open) {
      releaseRef.current = claimOpenDropdown((closeOptions) => handlers.current.close(closeOptions));
      setOpen(true);
    }
    focusWithoutScroll(triggerRef.current);
  }

  function choose(index) {
    const item = items[index];
    if (!item || item.disabled) return;
    close();
    setInvalidFromForm(false);
    if (item.value !== stringValue) onChange?.(item.value);
  }

  function typeAhead(character, fromIndex = activeIndex) {
    const enabled = items.map((item, index) => (item.disabled ? -1 : index)).filter((index) => index >= 0);
    const now = Date.now();
    const match = typeAheadMatch({
      labels: enabled.map((index) => items[index].label),
      activeIndex: Math.max(0, enabled.indexOf(fromIndex)),
      buffer: typeAheadRef.current.buffer,
      lastTypedAt: typeAheadRef.current.at,
      character,
      now,
    });
    typeAheadRef.current = { buffer: match.buffer, at: now };
    if (match.index >= 0) setActiveIndex(enabled[match.index]);
  }

  // Window listeners read the latest render through this ref.
  const handlers = useRef(null);
  handlers.current = {
    close,
    choose,
    typeAhead,
    step: (direction) => setActiveIndex((current) => steppedSelectIndex(items, current, direction)),
    edge: (edge) => setActiveIndex(steppedSelectIndex(items, -1, edge === 'first' ? 1 : -1)),
    typingAhead: () => Boolean(typeAheadRef.current.buffer) && Date.now() - typeAheadRef.current.at <= TYPE_AHEAD_RESET_MS,
    activeIndex,
    satisfied,
    required,
    disabled,
  };

  // Name the combobox from its visible label, without the value text.
  useIsomorphicLayoutEffect(() => {
    if (ariaLabel || ariaLabelledBy) return;
    const text = labelTextFor(triggerRef.current);
    if (text !== derivedLabel) setDerivedLabel(text);
  });

  useEffect(() => {
    if (autoFocus) focusWithoutScroll(triggerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => releaseRef.current?.(), []);

  useEffect(() => {
    if (satisfied) setInvalidFromForm(false);
  }, [satisfied]);

  useEffect(() => {
    if (open && disabled) close({ restoreFocus: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, disabled]);

  // `required`: block the enclosing form's submit while nothing valid is
  // chosen. A target-phase listener on the form runs before React's
  // delegated onSubmit at the root, so the caller's handler never sees it.
  useEffect(() => {
    if (!required) return undefined;
    const form = triggerRef.current?.closest?.('form');
    if (!form) return undefined;
    function handleSubmit(event) {
      const current = handlers.current;
      if (!current.required || current.disabled || current.satisfied) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setInvalidFromForm(true);
      focusWithoutScroll(triggerRef.current);
    }
    form.addEventListener('submit', handleSubmit, true);
    return () => form.removeEventListener('submit', handleSubmit, true);
  }, [required]);

  // Re-measure when the options change while open (an async refresh).
  useIsomorphicLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    showInTopLayer(panelRef.current);
    positionDropdown(panelRef.current, triggerRef.current, 'select');
  }, [open, items.length]);

  useEffect(() => {
    if (!open || activeIndex < 0 || typeof document === 'undefined') return;
    document.getElementById(`${listId}-option-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, listId]);

  useEffect(() => {
    if (!open) return undefined;
    const unsubscribe = subscribeDropdownDismissal({
      anchor: triggerRef.current,
      // The field's <label> counts as inside: it forwards its click to the
      // trigger, which then toggles the list closed.
      isInside: (target) => Boolean(
        triggerRef.current?.contains(target)
        || panelRef.current?.contains(target)
        || [...(triggerRef.current?.labels || [])].some((label) => label.contains(target)),
      ),
      isInsidePanel: (target) => Boolean(panelRef.current?.contains(target)),
      close: (closeOptions) => handlers.current.close(closeOptions),
    });
    function handleKeyDown(event) {
      const current = handlers.current;
      if (event.key === 'Escape') {
        // Capture phase: Escape closes the list, not an enclosing dialog.
        event.preventDefault();
        event.stopPropagation();
        current.close();
        return;
      }
      if (event.key === 'Tab') {
        current.close({ restoreFocus: false });
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey || event.target !== triggerRef.current) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        current.step(event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        current.edge(event.key === 'Home' ? 'first' : 'last');
      } else if (event.key === ' ' && current.typingAhead()) {
        // A space inside an in-progress type-ahead ("New Y") keeps searching.
        event.preventDefault();
        current.typeAhead(' ');
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        // A button activates on Space keyup; swallow it so choosing does not
        // immediately reopen the list.
        if (event.key === ' ') suppressNextSpaceKeyUp();
        current.choose(current.activeIndex);
      } else if (event.key.length === 1) {
        event.preventDefault();
        current.typeAhead(event.key);
      }
    }
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      unsubscribe();
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open]);

  function handleTriggerKeyDown(event) {
    if (open || event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openList('selected');
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      openList(event.key === 'Home' ? 'first' : 'last');
    } else if (event.key.length === 1 && event.key !== ' ') {
      event.preventDefault();
      openList('selected');
      // The list's active option is not applied until the next render.
      typeAhead(event.key, selectedIndex);
    }
  }

  const invalid = ariaInvalid === true || ariaInvalid === 'true' || invalidFromForm;
  const describedBy = [ariaDescribedBy, invalidFromForm ? errorId : ''].filter(Boolean).join(' ') || undefined;
  const listbox = (
    <div
      ref={panelRef}
      id={listId}
      role="listbox"
      // Named like its field while open; unnamed while hidden so label
      // queries resolve to the trigger alone.
      aria-label={open ? accessibleName : undefined}
      aria-labelledby={open && !accessibleName ? ariaLabelledBy : undefined}
      className="bl-dropdown-panel"
      hidden={!open}
      popover={open ? 'manual' : undefined}
      // Keep focus on the combobox while the pointer chooses.
      onPointerDown={(event) => event.preventDefault()}
    >
      {groupedSegments(items).map((segment, segmentIndex) => {
        const rows = segment.entries.map(({ item, index }) => (
          <div
            key={`${item.value}-${index}`}
            id={`${listId}-option-${index}`}
            role="option"
            aria-selected={index === selectedIndex ? 'true' : 'false'}
            aria-disabled={item.disabled ? 'true' : undefined}
            data-value={item.value}
            className={`bl-dropdown-option${open && index === activeIndex ? ' is-active' : ''}`}
            onPointerMove={() => { if (!item.disabled && index !== activeIndex) setActiveIndex(index); }}
            onClick={() => choose(index)}
          >
            <span className="bl-dropdown-option-label">{item.label}</span>
            <span className="bl-dropdown-check" aria-hidden="true">{index === selectedIndex ? <CheckIcon /> : null}</span>
          </div>
        ));
        if (!segment.group) return rows;
        const labelId = `${listId}-group-${segmentIndex}`;
        return (
          <div key={labelId} role="group" aria-labelledby={labelId} className="bl-dropdown-group">
            <div id={labelId} role="presentation" className="bl-dropdown-group-label">{segment.group}</div>
            {rows}
          </div>
        );
      })}
    </div>
  );
  const portalTarget = open ? dropdownPortalTarget(triggerRef.current) : null;

  return (
    <span className={`bl-select${className ? ` ${className}` : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        className={`bl-select-trigger${triggerClassName ? ` ${triggerClassName}` : ''}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={listId}
        aria-activedescendant={open && activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined}
        aria-label={accessibleName}
        aria-labelledby={accessibleName ? undefined : ariaLabelledBy}
        aria-describedby={describedBy}
        aria-invalid={invalid ? 'true' : undefined}
        aria-required={required ? 'true' : undefined}
        data-value={selected ? selected.value : ''}
        data-placeholder={selected ? undefined : 'true'}
        title={title}
        style={style}
        disabled={disabled}
        onClick={() => (open ? close() : openList('selected'))}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="bl-select-value">{displayLabel}</span>
        <span className="bl-select-chevron" aria-hidden="true"><ChevronIcon /></span>
      </button>
      {open && portalTarget ? createPortal(listbox, portalTarget) : listbox}
      {invalidFromForm ? <span id={errorId} className="bl-select-error" role="alert">{requiredMessage}</span> : null}
    </span>
  );
}
